import React, { useEffect, useRef, useState } from "react";
import { Button, Chip, IconButton } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import cls from "classnames";
import { DURATIONS, formatAmount } from "../../../pages/travelResult/searchResults";
import styles from "./ResultsFilters.module.css";

const DURATION_FALLBACKS = {
  short: "≤ 5 jours",
  medium: "6–8 jours",
  long: "9–11 jours",
  xl: "12 jours et +",
};

export const durationLabel = (t, value) =>
  t(`trips.results.durations.${value}`, DURATION_FALLBACKS[value]);

/**
 * Filtres de la page résultats (.filters-panel) : durée et budget maximum.
 * Colonne fixe sur grand écran, tiroir latéral en dessous de 960px
 * (`drawer` + `open` + `onClose`).
 */
const ResultsFilters = ({
  durations,
  maxPrice,
  priceBounds,
  onChange,
  onReset,
  drawer = false,
  open = false,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-US" : "fr-FR";
  const panelRef = useRef(null);

  /* Le curseur bouge librement ; l'URL n'est mise à jour qu'au relâchement. */
  const boundMax = priceBounds?.max || 0;
  const [budget, setBudget] = useState(maxPrice || boundMax);
  useEffect(() => {
    setBudget(maxPrice && maxPrice < boundMax ? maxPrice : boundMax);
  }, [maxPrice, boundMax]);

  const commitBudget = () => {
    const value = Number(budget);
    onChange({ maxPrice: value >= boundMax ? null : value });
  };

  const toggleDuration = (value, checked) => {
    const next = checked ? [...durations, value] : durations.filter((d) => d !== value);
    onChange({ duration: DURATIONS.map((d) => d.value).filter((v) => next.includes(v)) });
  };

  /* Tiroir : Échap ferme, focus sur le panneau, pas de défilement dessous. */
  useEffect(() => {
    if (!drawer || !open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [drawer, open, onClose]);

  const hasFilters = durations.length > 0 || Boolean(maxPrice);

  return (
    <>
      {drawer && (
        <div
          className={cls(styles.overlay, { [styles.overlayVisible]: open })}
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        ref={panelRef}
        id="results-filters"
        className={cls(styles.panel, { [styles.drawer]: drawer, [styles.open]: drawer && open })}
        aria-label={t("trips.results.filtersAria", "Filtres de recherche")}
        tabIndex={drawer ? -1 : undefined}
        aria-hidden={drawer && !open ? true : undefined}
        inert={drawer && !open ? "" : undefined}
      >
        <div className={styles.head}>
          <h2 className={styles.title}>{t("trips.results.filters", "Filtres")}</h2>
          {drawer && (
            <IconButton
              variant="outline"
              size="sm"
              iconName="iconClose"
              aria-label={t("trips.results.closeFilters", "Fermer les filtres")}
              onClick={onClose}
            />
          )}
        </div>

        <fieldset className={styles.group}>
          <legend className={styles.legend}>{t("trips.results.duration", "Durée")}</legend>
          <div className={styles.pills}>
            {DURATIONS.map((d) => (
              <Chip
                key={d.value}
                name="duration"
                value={d.value}
                label={durationLabel(t, d.value)}
                checked={durations.includes(d.value)}
                onChange={(checked) => toggleDuration(d.value, checked)}
              />
            ))}
          </div>
        </fieldset>

        {priceBounds && priceBounds.max > priceBounds.min && (
          <fieldset className={styles.group}>
            <legend className={styles.legend}>{t("trips.results.budget", "Budget max")}</legend>
            <input
              className={styles.range}
              type="range"
              min={priceBounds.min}
              max={priceBounds.max}
              step={100}
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
              onPointerUp={commitBudget}
              onKeyUp={commitBudget}
              onBlur={() => Number(budget) !== (maxPrice || boundMax) && commitBudget()}
              aria-label={t("trips.results.budgetAria", "Budget maximum par personne")}
              aria-valuetext={formatAmount(budget, priceBounds.currency, locale)}
            />
            <div className={styles.rangeValues}>
              <span>{formatAmount(priceBounds.min, priceBounds.currency, locale)}</span>
              <output className={styles.rangeOutput}>
                ≤ {formatAmount(budget, priceBounds.currency, locale)}
              </output>
            </div>
          </fieldset>
        )}

        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="medium"
            fullWidth
            disabled={!hasFilters}
            label={t("trips.results.resetFilters", "Réinitialiser les filtres")}
            onClick={onReset}
          />
        </div>
      </aside>
    </>
  );
};

export default ResultsFilters;
